import {
    Input,
    Select,
    DatePicker,
    RadioButtonGroup,
    Conditional,
} from "@components/fields";
import { BRAND_OPTIONS } from "@product-pages/model/options/brand";
import { CAR_OPTIONS } from "@product-pages/model/options/car-type";
import { carType } from "@products/necst-ratenkredit/model/types";
import {
    Button,
    ContentSection,
    Fieldset,
    FormHeading,
    Heading,
    Layout,
    Paragraph,
    SectionHeading,
} from "@vwfs-bronson/bronson-react";
import { useTranslation } from "react-i18next";
import { WIZARD_FORM_FIELDS } from "@product-pages/model/wizard-form-fields";
import { adjustAmountValue } from "@components/common/amount";
import { useField } from "formik";

export const LandingForm = ({ setShowCarSectionForm, formikProps }: any) => {
    const { t } = useTranslation();
    const [isUsed, ,] = useField<keyof typeof CAR_OPTIONS>(WIZARD_FORM_FIELDS.vehicleData.isUsed().fieldName);
    return (
        <>
            <ContentSection className="u-pb-none">
                <Heading>{t("landing:title")}</Heading>
                <SectionHeading level={2}>{t("landing:subheading")}</SectionHeading>
                <Paragraph>{t("landing:subtitle")}</Paragraph>
            </ContentSection>
            <ContentSection pageWrap className="u-pb-none">
                <Fieldset>
                    <FormHeading>{t("landing:vehicleSection:title")}</FormHeading>
                    <Paragraph>{t("landing:vehicleSection:subtitle")}</Paragraph>
                    <Fieldset.Row>
                        <Layout>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* manufacturer */}
                                <Select
                                    formField={WIZARD_FORM_FIELDS.vehicleData.model.manufacturer()}
                                    label={t("landing:vehicleSection:fields:brand:label")}
                                    options={BRAND_OPTIONS}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* name - modelShort*/}
                                <Input
                                    infoIconText={t(
                                        "landing:vehicleSection:fields:modelShort:info"
                                    )}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.model.name()}
                                    label={t("landing:vehicleSection:fields:modelShort:label")}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* descriptionLong - descriptionLong */}
                                <Input
                                    infoIconText={t(
                                        "landing:vehicleSection:fields:modelLong:info"
                                    )}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.model.descriptionLong()}
                                    label={t("landing:vehicleSection:fields:modelLong:label")}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* year */}
                                <Input
                                    infoIconText={t("landing:vehicleSection:fields:year:info")}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.model.year()}
                                    label={t("landing:vehicleSection:fields:year:label")}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* recommendedRetailPriceAmount */}
                                <Input
                                    infoIconText={t(
                                        "landing:vehicleSection:fields:priceList:info"
                                    )}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.recommendedRetailPriceAmount.amount()}
                                    label={t("landing:vehicleSection:fields:priceList:label")}
                                    adjustValueOnBlur={adjustAmountValue}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* retailPriceAmount */}
                                <Input
                                    infoIconText={t(
                                        "landing:vehicleSection:fields:price:info"
                                    )}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.retailPriceAmount.amount()}
                                    label={t("landing:vehicleSection:fields:price:label")}
                                    adjustValueOnBlur={adjustAmountValue}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* isUsed */}
                                <RadioButtonGroup
                                    formField={WIZARD_FORM_FIELDS.vehicleData.isUsed()}
                                    label={t("landing:vehicleSection:fields:usedCar:label")}
                                    options={CAR_OPTIONS}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* deliveryDate */}
                                <DatePicker
                                    infoIconText={t(
                                        "landing:vehicleSection:fields:deliveryDate:info"
                                    )}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.deliveryData.deliveryDate()}
                                    label={t("landing:vehicleSection:fields:deliveryDate:label")}
                                />
                            </Layout.Item>
                            <Conditional
                                when={WIZARD_FORM_FIELDS.vehicleData.isUsed()}
                                is={(isUsed) => isUsed === carType.USED}
                                onHide={(reset) =>
                                    reset([
                                        WIZARD_FORM_FIELDS.vehicleData.initialRegistrationDate(), WIZARD_FORM_FIELDS.vehicleData.vin()]
                                    )
                                }
                            >
                                <Layout.Item default="1/2" xs="1/1">
                                    {/* Only when Gebrauchtwagen is selected */}
                                    {/* vin */}
                                    <Input
                                        infoIconText={t(
                                            "landing:vehicleSection:fields:vin:info"
                                        )}
                                        formField={WIZARD_FORM_FIELDS.vehicleData.vin()}
                                        label={t("landing:vehicleSection:fields:vin:label")}
                                    />
                                </Layout.Item>
                                <Layout.Item default="1/2" xs="1/1">
                                    <DatePicker
                                        formField={WIZARD_FORM_FIELDS.vehicleData.initialRegistrationDate()}
                                        label={t(
                                            "landing:vehicleSection:fields:firstRegistrationDate:label"
                                        )}
                                    />
                                </Layout.Item>
                            </Conditional>
                            <Conditional
                                when={WIZARD_FORM_FIELDS.vehicleData.isUsed()}
                                is={(isUsed) => isUsed === carType.USED}
                                onHide={(reset) =>
                                    reset([WIZARD_FORM_FIELDS.vehicleData.odometer()])
                                }
                            >
                                <Layout.Item default="1/2" xs="1/1">
                                    {/* Only when Gebrauchtwagen is selected */}
                                    {/* odometer */}
                                    <Input
                                        formField={WIZARD_FORM_FIELDS.vehicleData.odometer()}
                                        label={t("landing:vehicleSection:fields:mileage:label")}
                                    />
                                </Layout.Item>
                            </Conditional>
                            <Layout.Item default="1/2" xs="1/1">
                                <DatePicker
                                    {...(isUsed?.value && { infoIconText: t(`landing:vehicleSection:fields:orderDate:info-${CAR_OPTIONS?.[isUsed?.value] === CAR_OPTIONS.USED ? 'USED' : 'NEW'}`) })}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.orderDate()}
                                    label={t("landing:vehicleSection:fields:orderDate:label")}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* manufacturerCode */}
                                <Input
                                    formField={WIZARD_FORM_FIELDS.vehicleData.model.manufacturerCode()}
                                    label={t(
                                        "landing:vehicleSection:fields:manufacturerKeyNumber:label"
                                    )}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* typeKeyNumber */}
                                <Input
                                    formField={WIZARD_FORM_FIELDS.vehicleData.model.typeCode()}
                                    label={t("landing:vehicleSection:fields:typeKeyNumber:label")}
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                {/* code */}
                                <Input
                                    infoIconText={t(
                                        "landing:vehicleSection:fields:modelTypeKey:info"
                                    )}
                                    formField={WIZARD_FORM_FIELDS.vehicleData.model.code()}
                                    label={t("landing:vehicleSection:fields:modelTypeKey:label")}
                                />
                            </Layout.Item>
                        </Layout>
                    </Fieldset.Row>
                    <Fieldset.Row>
                        <Layout>
                            <Layout.Item default="1/2" xs="1/1">
                                <div className="c-footnotes u-pl-none">
                                    {t("landing:vehicleSection:infoButton")}
                                </div>
                            </Layout.Item>
                            <Layout.Item default="1/2" xs="1/1">
                                <Button
                                    type="button"
                                    full
                                    onClick={async () => {
                                        await formikProps.submitForm();
                                        if (formikProps.isValid) {
                                            setShowCarSectionForm(false);
                                        }
                                    }}
                                >
                                    {t("landing:vehicleSection:submitButton")}
                                </Button>
                            </Layout.Item>
                        </Layout>
                    </Fieldset.Row>
                </Fieldset>
            </ContentSection>
        </>
    );
};
export default LandingForm;
